import { useQuery } from "@tanstack/react-query";

import { MemberSupplementsResponse, fetchMemberSupplements } from "~/api/members";

/**
 * Get query key for member supplements
 * @param groupMemberId The group member ID
 * @returns Query key array
 */
export function getMemberSupplementsQueryKey(groupMemberId?: number) {
  return ["memberSupplements", groupMemberId];
}

/**
 * Custom hook to fetch and cache member supplements data
 * @param groupMemberId The group member ID
 * @returns Query result containing member supplements data
 */
export function useMemberSupplements(groupMemberId?: number) {
  return useQuery<MemberSupplementsResponse>({
    queryKey: getMemberSupplementsQueryKey(groupMemberId),
    queryFn: () => {
      if (!groupMemberId) {
        // Return an empty structure if no group member ID is provided
        return Promise.resolve({
          recommendations: [],
          currentSupplements: [],
          recentSupplements: []
        });
      }
      return fetchMemberSupplements(groupMemberId);
    },
    enabled: !!groupMemberId, // Only run the query if groupMemberId exists
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: false, // Don't retry on failure - prevents infinite loop for new users
    retryOnMount: false // Don't retry on component mount
  });
}
